/**
 * Swym Cart Integration
 * Integrates Swym wishlist "Add to Cart" events with the Rose-the-watch-bar cart system
 * Handles complete cart UI updates after successful add-to-cart actions
 */

function onSwymLoadCallback(swat) {
    swat.evtLayer.addEventListener(swat.JSEvents.UIAddedToCart, function(eventData) {
        /**
         * Swym "Add to Cart" Event Handler
         * Triggered when a product is added to cart from Swym wishlist
         * 
         * Event Data Structure:
         * {
         *   product: {id, title, ...},
         *   variant: {id, ...},
         *   quantity: number
         * }
         */

        // Extract cart refresh logic
        handleSwymAddToCart(eventData);
    });
}

/**
 * Main handler for Swym add to cart events
 * Performs all necessary UI updates after item is added to cart
 */
async function handleSwymAddToCart(eventData) {
    try {
        // 1. GET CURRENT CART STATE
        const currentCart = window.xoCart ? window.xoCart : getCartState();
        
        // 2. UPDATE CART SECTIONS (Fetch updated HTML from Shopify)
        await updateCartSections();
        
        // 3. OPEN CART DRAWER/MODAL (async to allow DOM refresh)
        await openCartDrawer();
        
        // 4. TRIGGER CART FLY ANIMATION (optional visual feedback)
        triggerCartFlyAnimation(eventData);
        
        // 5. REFRESH CART NOTIFICATION DISPLAY
        refreshCartNotification();
        
        // 6. DISPATCH CUSTOM EVENT FOR EXTERNAL SYSTEMS
        document.dispatchEvent(new CustomEvent('swym:cart-updated', {
            detail: {
                variantId: eventData.variant?.id,
                quantity: eventData.quantity,
                productId: eventData.product?.id,
                timestamp: new Date().toISOString()
            }
        }));
        
        console.log('[Swym Cart] Add to cart successful', eventData);
    } catch (error) {
        console.error('[Swym Cart] Error during add to cart process:', error);
        handleCartError(error);
    }
}

/**
 * Fetches and updates all cart sections
 * This mimics the behavior of ws() and vs.addCart() functions
 */
async function updateCartSections() {
    try {
        // 1. FETCH FRESH CART DATA
        let cartData = null;
        try {
            cartData = await fetch(`${window.Shopify.routes.root}cart.js`, { cache: 'no-store' }).then((r) => r.json());
            console.log('[Swym Cart] Fetched fresh cart data:', cartData);
        } catch (err) {
            console.warn('[Swym Cart] Could not fetch cart.js:', err);
            return;
        }

        // 2. FETCH FRESH PAGE HTML
        const pageUrl = window.location.href.split('?')[0];
        const pageHtml = await fetch(pageUrl, { cache: 'no-store' }).then((r) => r.text());
        const parser = new DOMParser();
        const doc = parser.parseFromString(pageHtml, 'text/html');

        // 3. BUILD SECTIONS OBJECT - Extract fresh HTML for each section
        const sections = {};
        const sectionMap = {
            'xo-cart-mini': 'cart_mini',
            'xo-cart': 'cart',
            '#cart-icon-bubble': 'cart_icon',
        };

        for (const [selector, key] of Object.entries(sectionMap)) {
            const newEl = doc.querySelector(selector);
            const curEl = document.querySelector(selector);
            
            if (newEl && curEl) {
                // Completely replace the element's HTML
                const newHtml = newEl.innerHTML;
                curEl.innerHTML = newHtml;
                
                // Copy all attributes from new element
                Array.from(newEl.attributes).forEach(attr => {
                    if (attr.name !== 'id') {
                        curEl.setAttribute(attr.name, attr.value);
                    }
                });
                
                // Store HTML in sections object
                sections[key] = newHtml;
                
                console.log(`[Swym Cart] Updated section ${selector} with fresh HTML`);
            }
        }

        // 4. UPDATE XOSTORE WITH NEW CART STATE
        // This is the critical step that triggers CartMini component re-render
        if (window.xoStore && typeof window.xoStore.set === 'function') {
            const newCartState = {
                addIdLoading: '',
                isAdded: true,
                variantId: cartData.items && cartData.items.length > 0 ? String(cartData.items[cartData.items.length - 1].id) : '',
                changeLineLoading: '',
                addErrorMessage: '',
                changeErrorMessage: '',
                size: cartData.item_count,
                sections: sections,
            };
            
            window.xoStore.set('xo-cart', newCartState);
            
            // Also dispatch a custom event to ensure all listeners get notified
            document.dispatchEvent(new CustomEvent('xo-cart:state-changed', {
                detail: { state: newCartState, cartData: cartData }
            }));
            
            console.log('[Swym Cart] xoStore set with complete cart state:', {
                size: cartData.item_count,
                itemCount: cartData.items ? cartData.items.length : 0,
                sections: Object.keys(sections),
            });
        } else {
            console.warn('[Swym Cart] window.xoStore not available');
        }

        return cartData;
    } catch (error) {
        console.error('[Swym Cart] Error updating cart sections:', error);
        throw error;
    }
}

/**
 * Gets the section IDs that need to be updated
 * Based on current page context (cart page vs product page)
 */
function getCartSectionsToUpdate() {
    // Legacy function - no longer actively used but kept for reference
    return {};
}

/**
 * Opens the cart drawer/modal to show the newly added item
 * Uses the internal ms() function logic
 */
async function openCartDrawer() {
    try {
        // Add a longer delay to ensure DOM has been fully updated and re-rendered
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Find the cart mini element
        const cartMini = document.querySelector('xo-cart-mini');
        if (!cartMini) {
            console.warn('[Swym Cart] Cart mini element not found');
            return;
        }
        
        // Find the parent modal or popover
        const modal = cartMini.closest('xo-modal') || cartMini.closest('xo-popover');
        if (!modal) {
            console.warn('[Swym Cart] Parent modal/popover not found');
            return;
        }
        
        // Get the modal/popover name
        const modalName = modal.getAttribute('xo-name') || '';
        
        // Open using appropriate API based on element type
        const tagName = modal.tagName.toLowerCase();
        
        // First, try using the window APIs
        if (tagName === 'xo-modal') {
            // Use Modal API - try multiple approaches
            if (window.xoModal && window.xoModal.open) {
                window.xoModal.open(modalName);
                console.log('[Swym Cart] Cart drawer opened via xoModal.open');
                return;
            }
        } else if (tagName === 'xo-popover') {
            // Use Popover API
            if (window.xoPopover && window.xoPopover.open) {
                window.xoPopover.open(modalName);
                console.log('[Swym Cart] Cart drawer opened via xoPopover.open');
                return;
            }
        }
        
        // Fallback: Try setting xo-open attribute and firing click on trigger
        modal.setAttribute('xo-open', 'true');
        
        // Also try clicking the modal trigger as a fallback
        const trigger = document.querySelector(`xo-modal-trigger[xo-name="${modalName}"], xo-popover-trigger[xo-name="${modalName}"]`);
        if (trigger) {
            trigger.click();
            console.log('[Swym Cart] Cart drawer opened via trigger click');
        } else {
            console.log('[Swym Cart] Cart drawer opened via xo-open attribute');
        }
    } catch (error) {
        console.error('[Swym Cart] Error opening cart drawer:', error);
    }
}

/**
 * Triggers the cart fly animation
 * Visual feedback showing item flying to cart icon
 */
function triggerCartFlyAnimation(eventData) {
    try {
        const cartFly = document.querySelector('xo-cart-fly');
        if (cartFly && typeof cartFly.handle === 'function') {
            // Try to find a source element for the fly animation (product card / product element). Fallback to cart icon.
            const srcEl =
                document.querySelector(`[xo-product-id="${eventData.product?.id}"]`) ||
                document.querySelector(`[data-product-id="${eventData.product?.id}"]`) ||
                document.querySelector('.product') ||
                document.querySelector('.product-card') ||
                document.querySelector('#cart-icon-bubble') ||
                document.body;

            const srcRect = srcEl.getBoundingClientRect ? srcEl.getBoundingClientRect() : { top: 0, left: 0, right: 0, bottom: 0, width: 0, height: 0 };

            const virtualElement = {
                getBoundingClientRect: () => srcRect,
            };

            cartFly.handle(virtualElement);
            console.log('[Swym Cart] Cart fly animation triggered');
        }
    } catch (error) {
        console.error('[Swym Cart] Error triggering cart fly animation:', error);
    }
}

/**
 * Refreshes cart notification display
 * Auto-hides notification after 3 seconds as per main.js logic
 */
function refreshCartNotification() {
    try {
        const notification = document.querySelector('#xo-notify');
        if (!notification) return;
        
        // Show notification
        notification.classList.remove('cart-drawer-hidden');
        
        // Auto-hide after 3 seconds
        setTimeout(() => {
            notification.classList.add('cart-drawer-hidden');
        }, 3000);
        
        console.log('[Swym Cart] Cart notification refreshed');
    } catch (error) {
        console.error('[Swym Cart] Error refreshing cart notification:', error);
    }
}

/**
 * Updates the store state for cart or other xo-components
 * This communicates with the state management system
 */
function updateStoreState(storeName, newState) {
    try {
        // If xoStore is available, use its API to set the store state so subscriptions fire
        if (window.xoStore && typeof window.xoStore.set === 'function') {
            try {
                window.xoStore.set(storeName, (state) => ({ ...state, ...newState }));
            } catch (err) {
                // fallback to event dispatch if set fails
                document.dispatchEvent(new CustomEvent(`${storeName}:updated`, { detail: newState }));
            }
            console.log(`[Swym Cart] xoStore.set called for ${storeName}`);
            return;
        }

        // Fallback: dispatch an event listeners in this project may listen to
        if (window.cartSubscribe && typeof window.cartSubscribe === 'function') {
            document.dispatchEvent(new CustomEvent(`${storeName}:updated`, { detail: newState }));
        }
        
        console.log(`[Swym Cart] Store state updated: ${storeName}`, newState);
    } catch (error) {
        console.error('[Swym Cart] Error updating store state:', error);
    }
}

/**
 * Handles cart operation errors
 * Shows error notification to user
 */
function handleCartError(error) {
    try {
        const errorMessage = error.message || 'Failed to add item to cart';
        
        // Show error notification if available
        if (window.xoNotification && typeof window.xoNotification.show === 'function') {
            window.xoNotification.show({
                content: errorMessage,
                className: 'xo-cart-add-error',
                placement: 'top-center',
                delay: 4000
            });
        } else {
            // Fallback: use alert
            console.error('[Swym Cart] Error:', errorMessage);
        }
    } catch (err) {
        console.error('[Swym Cart] Error handling cart error:', err);
    }
}

/**
 * Gets current cart state
 * Retrieves data from xo-cart store if available
 */
function getCartState() {
    try {
        if (window.xoCart && typeof window.xoCart === 'object') {
            return window.xoCart;
        }
        
        // Fallback: fetch from Shopify
        return fetch(`${window.Shopify.routes.root}cart.js`)
            .then(response => response.json());
    } catch (error) {
        console.error('[Swym Cart] Error getting cart state:', error);
        return null;
    }
}

/**
 * Initialize Swym callbacks
 * Registers the Swym event listener when Swym library loads
 */
if (!window.SwymCallbacks) {
    window.SwymCallbacks = [];
}

window.SwymCallbacks.push(onSwymLoadCallback);

// Optional: Add manual trigger function for testing
window.triggerSwymCartUpdate = function(eventData) {
    console.log('[Swym Cart] Manual cart update triggered', eventData);
    handleSwymAddToCart(eventData || {
        product: { id: 'test' },
        variant: { id: 'test' },
        quantity: 1
    });
};

console.log('[Swym Cart] Integration script loaded');
